import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, Info, XCircle, X } from 'lucide-react';
import { cn } from './cn';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /**
   * Auto-dismiss delay in ms. Pass 0 to persist until manually dismissed.
   * When omitted, the default depends on tone: `error` and `warning` toasts
   * persist (0) so failures aren't missed, while `success`/`info` auto-dismiss
   * after {@link DEFAULT_TOAST_DURATION}ms.
   */
  duration?: number;
}

/** Auto-dismiss delay (ms) for non-critical (`success`/`info`) toasts. */
export const DEFAULT_TOAST_DURATION = 4500;

/**
 * Resolves the effective auto-dismiss duration for a toast. An explicit
 * `duration` always wins; otherwise error/warning toasts persist (0) and the
 * rest use {@link DEFAULT_TOAST_DURATION}. Pure — exported for testing.
 */
export function resolveToastDuration(
  tone: ToastTone,
  duration?: number,
): number {
  if (duration !== undefined) return duration;
  return tone === 'error' || tone === 'warning' ? 0 : DEFAULT_TOAST_DURATION;
}

interface ToastRecord extends Required<Omit<ToastOptions, 'description'>> {
  id: string;
  description?: string;
}

interface ToastContextValue {
  toast: (opts: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 className="h-4 w-4 text-emerald-400" />,
  error: <XCircle className="h-4 w-4 text-red-400" />,
  warning: <AlertTriangle className="h-4 w-4 text-amber-400" />,
  info: <Info className="h-4 w-4 text-sky-400" />,
};

const BORDERS: Record<ToastTone, string> = {
  success: 'border-emerald-500/30',
  error: 'border-red-500/30',
  warning: 'border-amber-500/30',
  info: 'border-sky-500/30',
};

/** Wrap the app once; exposes {@link useToast}. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (opts: ToastOptions) => {
      const id =
        globalThis.crypto?.randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const tone = opts.tone ?? 'info';
      const record: ToastRecord = {
        id,
        title: opts.title,
        description: opts.description,
        tone,
        duration: resolveToastDuration(tone, opts.duration),
      };
      setToasts((prev) => [...prev, record]);
      if (record.duration > 0) {
        window.setTimeout(() => dismiss(id), record.duration);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              className={cn(
                'pointer-events-auto flex items-start gap-3 rounded-xl border bg-surface-2 px-4 py-3 shadow-pop animate-slide-up',
                BORDERS[t.tone],
              )}
            >
              <span className="mt-0.5 shrink-0">{ICONS[t.tone]}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-zinc-100">{t.title}</p>
                {t.description && (
                  <p className="mt-0.5 text-xs text-zinc-400">
                    {t.description}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                className="rounded-md p-0.5 text-zinc-500 transition-colors hover:text-zinc-300"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/** Access the toast dispatcher. Must be used within {@link ToastProvider}. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}
